import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Edit2 } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, TextArea, toast } from '../../components/common';
import { vendorPortalApi } from '../../services/api';
import './VendorPortal.css';

export default function VendorProfile() {
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState({});

    const { data: profile, isLoading } = useQuery({
        queryKey: ['vendorPortal', 'profile'],
        queryFn: () => vendorPortalApi.getProfile(),
        select: (res) => res.data?.data || {},
        onSuccess: (data) => setForm(data)
    });

    const updateMutation = useMutation({
        mutationFn: (data) => vendorPortalApi.updateProfile(data),
        onSuccess: () => {
            toast.success('Profile updated');
            queryClient.invalidateQueries(['vendorPortal', 'profile']);
            setIsEditing(false);
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        updateMutation.mutate(form);
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div className="vendor-profile">
            <PageHeader
                title="My Profile"
                subtitle="Manage your vendor profile"
                breadcrumbs={[
                    { label: 'Vendor Portal', path: '/vendor-portal' },
                    { label: 'Profile' }
                ]}
                actions={
                    !isEditing ? (
                        <Button icon={Edit2} onClick={() => setIsEditing(true)}>
                            Edit Profile
                        </Button>
                    ) : null
                }
            />

            <Card>
                <CardBody>
                    <div className="vp-profile-header">
                        <h2>Company Information</h2>
                    </div>

                    {isEditing ? (
                        <div className="settings-form">
                            <div className="vp-profile-grid">
                                <Input
                                    name="name"
                                    label="Company Name"
                                    value={form.name || ''}
                                    onChange={handleChange}
                                />
                                <Input
                                    name="taxId"
                                    label="Tax ID"
                                    value={form.taxId || ''}
                                    onChange={handleChange}
                                />
                                <Input
                                    name="phone"
                                    label="Phone"
                                    value={form.phone || ''}
                                    onChange={handleChange}
                                />
                                <Input
                                    name="email"
                                    label="Email"
                                    value={form.email || ''}
                                    onChange={handleChange}
                                />
                            </div>
                            <TextArea
                                name="address"
                                label="Address"
                                value={form.address || ''}
                                onChange={handleChange}
                                rows={2}
                            />
                            <div className="settings-actions">
                                <Button variant="secondary" onClick={() => setIsEditing(false)}>
                                    Cancel
                                </Button>
                                <Button icon={Save} onClick={handleSave} loading={updateMutation.isPending}>
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="vp-profile-grid">
                            <div className="vp-profile-item">
                                <label>Company Name</label>
                                <span>{profile?.name || '-'}</span>
                            </div>
                            <div className="vp-profile-item">
                                <label>Tax ID</label>
                                <span>{profile?.taxId || '-'}</span>
                            </div>
                            <div className="vp-profile-item">
                                <label>Phone</label>
                                <span>{profile?.phone || '-'}</span>
                            </div>
                            <div className="vp-profile-item">
                                <label>Email</label>
                                <span>{profile?.email || '-'}</span>
                            </div>
                            <div className="vp-profile-item" style={{ gridColumn: 'span 2' }}>
                                <label>Address</label>
                                <span>{profile?.address || '-'}</span>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}
